import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { datadogLogs } from "@datadog/browser-logs";
import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
import { getSiteValueByLocaleId } from "../utils/SiteUtils";
import { hasConsent, onConsentChange } from "../utils/cookieYesConsent";

// Track initialization state (module-level, persists across calls)
let gtmInitialized = false;
let ga4Initialized = false;
let datadogInitialized = false;
let amplitudeConfigured = false;

// Store consent listener cleanup
let consentCleanup = null;

/**
 * Initialize external services (called once on app mount)
 */
export const setupExternalServices = () => {
	// GTM must load unconditionally - it provides CookieYes consent manager
	initGTM();

	// Clean up previous listener if exists
	if (consentCleanup) {
		consentCleanup();
	}

	// If consent already granted, initialize analytics immediately
	if (hasConsent("analytics")) {
		initializeAnalyticsServices();
	}

	// Listen for future consent changes (grants and withdrawals)
	// Rejection takes precedence if both arrays contain "analytics" (defensive)
	consentCleanup = onConsentChange(({ accepted, rejected }) => {
		if (rejected.includes("analytics")) {
			disableAnalyticsServices();
		} else if (accepted.includes("analytics")) {
			initializeAnalyticsServices();
		}
	});
};

/**
 * Initialize consent-gated analytics services (called when consent granted)
 */
const initializeAnalyticsServices = () => {
	initGA4();
	initDatadog();
	window.amplitude?.setOptOut(false); // Clear any persisted opt-out before configuring
	configureAmplitude(null);
};

/**
 * Disable analytics services when consent is withdrawn mid-session.
 *
 * Note: Only Amplitude supports clean opt-out without page reload.
 * - GA4: No runtime opt-out API; events continue until page reload
 * - Datadog: No runtime opt-out API; logs continue until page reload
 */
const disableAnalyticsServices = () => {
	if (window.amplitude) {
		window.amplitude.setOptOut(true);
	}
};

/**
 * Update services with user data (called on auth state changes)
 */
export const setupExternalServicesWithUser = (user) => {
	if (!user) return;

	const {
		id: userId,
		email: userEmail,
		firstName: userName,
		phone: userPhone,
	} = user;

	// GTM dataLayer updates unconditionally (it's just local data, tags are consent-gated in GTM)
	updateGTMDataLayer(userId, userEmail, userName, userPhone);

	// Other services require consent
	if (hasConsent("analytics")) {
		configureAmplitude(userId);
		updateDatadogUser(userId, userEmail);
	}
};

/**
 * Initialize GTM (only once)
 */
const initGTM = () => {
	if (gtmInitialized) return;
	gtmInitialized = true;

	TagManager.initialize({
		gtmId: process.env.REACT_APP_GTM_ID,
		auth: process.env.REACT_APP_GTM_ENV_AUTH,
		preview: process.env.REACT_APP_GTM_ENV_PREVIEW,
	});
};

/**
 * Update GTM dataLayer (safe to call multiple times)
 */
const updateGTMDataLayer = (userId, userEmail, userName, userPhone) => {
	if (!gtmInitialized) return;

	TagManager.dataLayer({
		dataLayer: {
			user: {
				id: userId,
				email: userEmail,
				name: userName,
				phone: userPhone,
			},
			nodeEnv: process.env.NODE_ENV,
		},
	});
};

/**
 * Initialize GA4 (only once)
 */
const initGA4 = () => {
	if (ga4Initialized) return;
	ga4Initialized = true;

	const gaMeasurementId = getSiteValueByLocaleId("gaMeasurementId");
	ReactGA.initialize(gaMeasurementId);
};

/**
 * Initialize Datadog Logs (only once)
 */
const initDatadog = () => {
	if (datadogInitialized) return;
	datadogInitialized = true;

	datadogLogs.init({
		clientToken: "pubfe3de757580b6543d9d5412bb7f0fb22",
		site: "datadoghq.com",
		forwardErrorsToLogs: true,
		forwardConsoleLogs: ["log", "error"],
		sessionSampleRate: 100,
		service: "plt-web",
		env: process.env.REACT_APP_ENVIRONMENT,
	});
};

/**
 * Update Datadog with user context
 */
const updateDatadogUser = (userId, userEmail) => {
	if (!datadogInitialized) return;

	datadogLogs.setUser({
		id: userId,
		email: userEmail,
	});
};

/**
 * Configure Amplitude (assumes SDK loaded via GTM)
 */
const configureAmplitude = (userId) => {
	if (!window.amplitude) return;

	// Add session replay plugin once
	if (!amplitudeConfigured) {
		amplitudeConfigured = true;
		window.amplitude.add(
			sessionReplayPlugin({
				debugMode: process.env.REACT_APP_ENVIRONMENT !== "production",
			}),
		);
	}

	window.amplitude.setUserId(userId);
};
